import { useEffect, useState, useCallback, useRef } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import { causedByAbortError, type NoNetworkError } from '@confluence/network';
import { fg } from '@confluence/feature-gating';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';

import { fetchJiraProjects, fetchRecentJiraProjects } from './apiUtils';
import { isValidProject, isValidProjectFromLocalStorageQuery } from './jiraProjectsUtils';

export type UseGetJiraProjectsResult = {
	optionsRecentProjects: JiraProjects;
	optionsAllProjects: JiraProjects;
	selectedProject: JiraProject | null;
	isLoading: boolean;
	error: Error | null;
	onProjectChange: (project: JiraProject | null) => void;
};

export const useGetJiraProjectsForSingleIssueCreateForm = (
	selectedAvailableSite: AvailableSite | null = null,
	projectSelectorQuery: string | null = null,
	selectedProjectFromLocalStorage: JiraProject | null = null,
): UseGetJiraProjectsResult => {
	const [optionsRecentProjects, setOptionsRecentProjects] = useState<JiraProjects>([]);
	const [optionsAllProjects, setOptionsAllProjects] = useState<JiraProjects>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [selectedProject, setSelectedProject] = useState<JiraProject | null>(
		selectedProjectFromLocalStorage || null,
	);
	const [userHasSelectedProject, setUserHasSelectedProject] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);

	// Store state setters in refs to avoid recreating resetState
	const settersRef = useRef({
		setOptionsRecentProjects,
		setOptionsAllProjects,
		setSelectedProject,
		setError,
		setUserHasSelectedProject,
	});

	// Store current values in refs to access in effects without dependencies
	const currentValuesRef = useRef({
		selectedProject,
		userHasSelectedProject,
	});

	// Update refs when setters change
	useEffect(() => {
		settersRef.current = {
			setOptionsRecentProjects,
			setOptionsAllProjects,
			setSelectedProject,
			setError,
			setUserHasSelectedProject,
		};
	}, [
		setOptionsRecentProjects,
		setOptionsAllProjects,
		setSelectedProject,
		setError,
		setUserHasSelectedProject,
	]);

	// Update current values ref when state changes
	useEffect(() => {
		currentValuesRef.current = {
			selectedProject,
			userHasSelectedProject,
		};
	}, [selectedProject, userHasSelectedProject]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [selectedAvailableSite?.cloudId]);

	const resetState = useCallback(() => {
		settersRef.current.setOptionsRecentProjects([]);
		settersRef.current.setOptionsAllProjects([]);
		settersRef.current.setSelectedProject(null);
		settersRef.current.setError(null);
		settersRef.current.setUserHasSelectedProject(false);
	}, []);

	// Reset state when site changes
	useEffect(() => {
		resetState();
	}, [selectedAvailableSite?.cloudId, resetState]);

	// Fetch projects when site is available
	useEffect(() => {
		if (!selectedAvailableSite?.cloudId) {
			return;
		}

		// Abort any ongoing requests
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		// Create new abort controller
		abortControllerRef.current = new AbortController();

		const cloudId = selectedAvailableSite.cloudId;
		const query = projectSelectorQuery?.trim() || '';

		const fetchProjects = async () => {
			setIsLoading(true);
			setError(null);

			try {
				if (query) {
					// If there's a search query, only fetch filtered projects
					const searchResults = await fetchJiraProjects({
						cloudId,
						orderBy: 'name',
						query,
						signal: abortControllerRef.current?.signal,
					});
					setOptionsRecentProjects([]);
					setOptionsAllProjects(searchResults || []);

					// During search, don't change the selected project to keep the dropdown open
					// Only set a project if none is currently selected and user hasn't made a selection
					if (
						!currentValuesRef.current.selectedProject &&
						!currentValuesRef.current.userHasSelectedProject &&
						searchResults &&
						searchResults.length > 0
					) {
						// if localStorage is set, validate before using it, otherwise use the first project
						if (selectedProjectFromLocalStorage) {
							const isValid = await isValidProject(selectedProjectFromLocalStorage, searchResults);
							setSelectedProject(isValid ? selectedProjectFromLocalStorage : searchResults[0]);
						} else {
							setSelectedProject(searchResults[0]);
						}
					}
				} else {
					if (fg('jira_aiwc_confluence_improve_projects_fetch')) {
						const signal = abortControllerRef.current?.signal;
						const recentPromise = fetchRecentJiraProjects({ cloudId, signal }).then(
							async (recentProjects) => {
								const recentProjectIds = recentProjects?.map((project) => project.id) || [];
								if (recentProjectIds.length > 0 && !signal?.aborted) {
									const recentProjectsWithPermissions = await fetchJiraProjects({
										cloudId,
										orderBy: 'name',
										projectIds: recentProjectIds,
										signal,
									});
									setOptionsRecentProjects(recentProjectsWithPermissions || []);
									return recentProjectsWithPermissions;
								} else {
									return Promise.resolve([]);
								}
							},
						);
						const allPromise = fetchJiraProjects({ cloudId, orderBy: 'name', signal }).then(
							(allProjects) => {
								setOptionsAllProjects(allProjects || []);
								return allProjects;
							},
						);

						const [recentProjectsResult, allProjectsResult] = await Promise.allSettled([
							recentPromise,
							allPromise,
						]);

						// Only auto-select if user hasn't explicitly selected a project and no valid localStorage value exists
						if (!currentValuesRef.current.userHasSelectedProject) {
							// First check if localStorage has a valid project
							if (selectedProjectFromLocalStorage) {
								const isValidFromRecentProjects =
									recentProjectsResult.status === 'fulfilled' &&
									recentProjectsResult.value &&
									(await isValidProject(
										selectedProjectFromLocalStorage,
										recentProjectsResult.value,
									));

								if (isValidFromRecentProjects) {
									setSelectedProject(selectedProjectFromLocalStorage);
									return;
								}

								// if not in recent projects, check all projects
								const isValidFromAllProjects =
									allProjectsResult.status === 'fulfilled' &&
									allProjectsResult.value &&
									(await isValidProject(selectedProjectFromLocalStorage, allProjectsResult.value));

								if (isValidFromAllProjects) {
									setSelectedProject(selectedProjectFromLocalStorage);
									return;
								}

								// if not in recent or all projects, check via the query API
								const {
									isAvailable: isAvailableFromLocalStorageQuery,
									searchResults: projectsFromLocalStorageQuery,
								} = await isValidProjectFromLocalStorageQuery(
									selectedProjectFromLocalStorage,
									selectedAvailableSite,
									abortControllerRef.current,
								);

								if (isAvailableFromLocalStorageQuery) {
									// Add to "All Projects" so it's visible in dropdown for re-selection
									setOptionsAllProjects((prev) => {
										const existingIds = prev.map((p) => p.id);
										const newProjects = projectsFromLocalStorageQuery.filter(
											(p) => !existingIds.includes(p.id),
										);
										return [...prev, ...newProjects];
									});

									setSelectedProject(selectedProjectFromLocalStorage);
									// return to avoid setting the first project from the recent or all projects
									return;
								}
							}

							// Only auto-select if no valid localStorage value
							if (
								recentProjectsResult.status === 'fulfilled' &&
								recentProjectsResult.value &&
								recentProjectsResult.value.length > 0
							) {
								setSelectedProject(recentProjectsResult.value[0]);
							} else if (
								allProjectsResult.status === 'fulfilled' &&
								allProjectsResult.value &&
								allProjectsResult.value.length > 0
							) {
								setSelectedProject(allProjectsResult.value[0]);
							} else {
								setSelectedProject(null);
							}
						}
					} else {
						// Otherwise fetch both recent and all projects
						const [recentProjectsResult, allProjectsResult] = await Promise.allSettled([
							fetchRecentJiraProjects({ cloudId, signal: abortControllerRef.current?.signal }),
							fetchJiraProjects({
								cloudId,
								orderBy: 'name',
								signal: abortControllerRef.current?.signal,
							}),
						]);

						let recentProjectsWithPermissions: JiraProjects = [];

						// Handle recent projects
						if (
							recentProjectsResult.status === 'fulfilled' &&
							allProjectsResult.status === 'fulfilled' &&
							recentProjectsResult.value
						) {
							const recentProjectIds = recentProjectsResult.value.map((project) => project.id);
							if (recentProjectIds.length > 0) {
								recentProjectsWithPermissions =
									(await fetchJiraProjects({
										cloudId,
										orderBy: 'name',
										projectIds: recentProjectIds,
										signal: abortControllerRef.current?.signal,
									})) || [];

								setOptionsRecentProjects(recentProjectsWithPermissions);
							}
						}

						// Handle all projects
						if (allProjectsResult.status === 'fulfilled' && allProjectsResult.value) {
							const allProjects = allProjectsResult.value;
							setOptionsAllProjects(allProjects);

							// Only auto-select if user hasn't made a selection
							if (!currentValuesRef.current.userHasSelectedProject) {
								if (recentProjectsWithPermissions.length > 0) {
									setSelectedProject(recentProjectsWithPermissions[0]);
								} else if (allProjects.length > 0) {
									setSelectedProject(allProjects[0]);
								}
							}
						}
					}
				}
			} catch (e: Error | unknown) {
				if (causedByAbortError(e as NoNetworkError)) {
					setIsLoading(true);
					resetState();
					return;
				}
				setError(e as Error);
				resetState();
			} finally {
				setIsLoading(false);
			}
		};

		void fetchProjects();
	}, [
		selectedAvailableSite?.cloudId,
		projectSelectorQuery,
		resetState,
		selectedProjectFromLocalStorage,
		selectedAvailableSite,
	]);

	const handleProjectChange = useCallback((project: JiraProject | null) => {
		setSelectedProject(project);
		// Mark that user has explicitly selected a project
		setUserHasSelectedProject(true);
	}, []);

	return {
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject,
		isLoading,
		error,
		onProjectChange: handleProjectChange,
	};
};
